/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { Button, breakpoints } from '@coursera/cds-core';
import { BookmarkFilledIcon, BookmarkIcon } from '@coursera/cds-icons';

import TrackedButton from 'bundles/page/components/TrackedButton';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { SaveProductManager } from 'bundles/unified-description-page-common/utils/SaveProductManager';

import _t from 'i18n!nls/xdp';

const styles = {
  buttonOverrides: css`
    height: 40px;
    ${breakpoints.down('sm')} {
      padding-right: var(--cds-spacing-100);
      padding-left: var(--cds-spacing-100);
    }
  `,
  saveButtonText: css`
    ${breakpoints.down('sm')} {
      display: none;

      .cds-button-startIcon {
        margin-right: 0;
      }
    }
  `,
};

type Props = {
  isWishlisted?: boolean;
  productId: string;
  // Use saveProductFactory from SaveProductManager to get an instance of this class.
  saveProductManager: SaveProductManager;
};

const SaveButton = ({ isWishlisted, saveProductManager, productId }: Props) => {
  const [isSaved, setIsSaved] = useState(!!isWishlisted);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { enableXDPRedesignPhase1, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignHero;

  const saveProduct = async () => {
    try {
      setIsActionLoading(true);

      await saveProductManager.save(productId);

      setIsSaved(true);
      setIsActionLoading(false);
    } catch (err) {
      setIsActionLoading(false);
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const unSaveProduct = async () => {
    try {
      setIsActionLoading(true);

      await saveProductManager.unSave(productId);

      setIsSaved(false);
      setIsActionLoading(false);
    } catch (err) {
      setIsActionLoading(false);
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  let saveButtonLabel;
  if (!enableXDPRedesign) {
    saveButtonLabel = isSaved
      ? (saveButtonLabel = <span css={styles.saveButtonText}>{_t('Saved')}</span>)
      : (saveButtonLabel = <span css={styles.saveButtonText}>{_t('Save')}</span>);
  }

  const ariaLabel = isSaved ? _t('Remove from saved') : _t('Save');

  return (
    <Button
      aria-label={ariaLabel}
      requireFullyVisible={false}
      withVisibilityTracking={false}
      trackingName="save_button"
      data={{ productId }}
      component={TrackedButton}
      variant="ghost"
      disabled={isActionLoading}
      icon={isSaved ? <BookmarkFilledIcon /> : <BookmarkIcon />}
      iconPosition="before"
      css={styles.buttonOverrides}
      onClick={() => (isSaved ? unSaveProduct() : saveProduct())}
    >
      {saveButtonLabel}
    </Button>
  );
};

export default SaveButton;
